import { Box, Chip, Divider, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { nanoid } from '@reduxjs/toolkit';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { representationSchemeType } from 'components/ErrorsCorrections/ErrorsTable/constants';
import { DiscountItemTableDialog } from './DiscountItemTableDialog';
import { TOrderDiscountsItemProps } from '../types';
import { Duties } from '../../OrderSplitsCard/OrderDetailsRowChild/Duties';
import { Total } from '../../OrderSplitsCard/OrderDetailsRowChild/Total';

export const OrderDiscountsItem: FC<TOrderDiscountsItemProps> = ({
	orderDiscountItem,
	paymentCurrency,
	orderDiscountBreakdown,
}) => {
	const [detalization, setDetalization] = useState(false);
	const [openDiscountDialog, setOpenDiscountDialog] = useState(false);

	return (
		<Box>
			<Box
				sx={{
					border: '1px solid #00000020',
					borderRadius: '12px',
					padding: '10px',
					marginBottom: '10px',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 2,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: 1,
						}}
					>
						<Typography variant="body2" color="text.secondary">
							{orderDiscountItem?.timestamp &&
								DateTime.fromISO(orderDiscountItem.timestamp).toFormat('yyyy-MM-dd, HH:mm')}
						</Typography>
						{orderDiscountItem.order_status && (
							<Chip
								label={orderDiscountItem.order_status.toLowerCase().replaceAll('_', ' ')}
								color="primary"
								size="small"
								sx={{ p: '0 5px', ml: 1, fontSize: '.7rem', height: '20px' }}
							/>
						)}
					</Box>

					<Typography
						variant="body2"
						color="primary"
						sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}
						onClick={() => {
							setOpenDiscountDialog(true);
						}}
					>
						Show Split Item Table
					</Typography>
				</Box>

				{orderDiscountBreakdown.map((child) => (
					<Box sx={{ ml: 2, mb: 1 }} key={nanoid()}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'space-between',
								gap: 2,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									gap: 1,
								}}
							>
								<Typography
									variant="body2"
									sx={{
										fontWeight: 'bold',
									}}
									color="primary"
								>
									{representationSchemeType?.[
										child.rep_scheme as keyof typeof representationSchemeType
									] ?? child.rep_scheme}
								</Typography>
								<Typography variant="body2">
									<span style={{ fontWeight: 'bold' }}>{child.id_provided_by_em} — </span>
									{child.short_description}
								</Typography>
								<Chip
									label={`${String(child.quantity)} discounted`}
									size="small"
									sx={{
										p: '0 2px',
										fontSize: '.7rem',
										height: '20px',
										bgcolor: '#A7D6FF',
									}}
								/>
							</Box>

							{detalization ? (
								<Box
									sx={{
										display: 'flex',
										gap: 1,
									}}
								>
									<Typography
										variant="body2"
										sx={{
											fontSize: '.8rem',
										}}
										color="text.secondary"
									>
										<AmountAndCurrency
											amount={child.discount_amount / child.quantity}
											currency={paymentCurrency}
											bold="bold"
											size={0.8}
											color="text.secondary"
										/>
										&nbsp;&times;&nbsp;{child.quantity}&nbsp;=&nbsp;
									</Typography>
									<AmountAndCurrency
										amount={child.discount_amount}
										currency={paymentCurrency}
										bold="bold"
										size={0.9}
										color="text.secondary"
									/>
								</Box>
							) : (
								<AmountAndCurrency
									amount={child.discount_amount ?? 0}
									currency={paymentCurrency}
									bold="bold"
									size={1}
								/>
							)}
						</Box>
						{detalization && (
							<>
								<Duties
									duty={child.discount_on_cost}
									nameOfDuty="Discount on Cost"
									paymentCurrency={paymentCurrency}
								/>
								{!!child.discount_on_vat && (
									<Duties
										duty={child.discount_on_vat}
										nameOfDuty="Discount on VAT"
										paymentCurrency={paymentCurrency}
									/>
								)}

								<Divider sx={{ mt: 0.4, mb: 0.4, ml: 4 }} />
								<Total
									totalPerItem={child.discount_amount ?? 0}
									quantity={child.quantity}
									totalCost={child.discount_amount ?? 0}
									paymentCurrency={paymentCurrency}
								/>
							</>
						)}
					</Box>
				))}
				{orderDiscountBreakdown.length > 1 && (
					<>
						<Divider sx={{ mt: 1, mb: 1, ml: 4 }} />

						<Box sx={{ display: 'flex', justifyContent: 'end' }}>
							<AmountAndCurrency
								amount={orderDiscountItem.total_discount_amount}
								currency={paymentCurrency}
								bold="bold"
								size={1}
							/>
						</Box>
					</>
				)}
				<DiscountItemTableDialog
					open={openDiscountDialog}
					onClose={() => setOpenDiscountDialog(false)}
					title={`Discount Items – ${orderDiscountItem.external_order_id ?? orderDiscountItem.id}`}
					items={orderDiscountBreakdown}
					loading={false}
					currency={paymentCurrency}
				/>
			</Box>
		</Box>
	);
};
